import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RefreshCw,
  Wallet,
  Eye,
  AlertCircle,
  CreditCard,
  X,
  XCircle,
  Download,
  ExternalLink,
  QrCode,
  CheckCircle,
  Clock,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import WalletTopup from '@/components/WalletTopup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_PAYMENT_URL = 'https://razorpay.me/@cloudsetuprivatelimited';

const fmt = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'numeric', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-IN', {
    month: 'numeric', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const StatusBadge = ({ status }) => {
  const map = {
    paid:      'bg-green-100 text-green-700 border border-green-200',
    unpaid:    'bg-red-100 text-red-600 border border-red-200',
    pending:   'bg-amber-100 text-amber-700 border border-amber-200',
    cancellation_requested: 'bg-amber-50 text-amber-700 border border-amber-200',
    voided:    'bg-slate-100 text-slate-500 border border-slate-200',
    refunded:  'bg-blue-100 text-blue-700 border border-blue-200',
    processed: 'bg-green-100 text-green-700 border border-green-200',
  };
  const cls = map[status] || map.pending;
  return (
    <span className={`px-2.5 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : '-'}
    </span>
  );
};

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`relative pb-3 text-sm transition-colors whitespace-nowrap ${
      active
        ? 'text-blue-700 font-medium after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
        : 'text-slate-500 hover:text-slate-700'
    }`}
  >
    {children}
  </button>
);

const PillBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
      active
        ? 'bg-white border-blue-500 text-blue-700 shadow-sm'
        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-400'
    }`}
  >
    {children}
  </button>
);

export default function ClientBilling() {
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get('tab');
  const VALID_TABS = ['account', 'billing'];

  const [topTab, setTopTab] = useState(VALID_TABS.includes(urlTab) ? urlTab : 'account');

  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab)) {
      setTopTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  const [txTab, setTxTab] = useState('invoices');

  // ── domain orders ──
  const [domainOrders, setDomainOrders] = useState([]);
  const [domainOrdersLoading, setDomainOrdersLoading] = useState(false);

  // ── data ──
  const [wallet, setWallet] = useState({ balance: 0, locked_funds: 0 });
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [clientInfo, setClientInfo] = useState({});
  const [isAdminDirectClient, setIsAdminDirectClient] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── payment settings ──
  const [autoPayCard, setAutoPayCard] = useState(false);
  const [walletConsentEnabled, setWalletConsentEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // ── card placeholder modal ──
  const [cardModalOpen, setCardModalOpen] = useState(false);

  // ── coupon ──
  const [clientCouponStatus, setClientCouponStatus] = useState(null);
  const [clientCouponCode, setClientCouponCode] = useState('');
  const [clientCouponSubmitting, setClientCouponSubmitting] = useState(false);

  // ── active service offer ──
  const [activeServiceOffer, setActiveServiceOffer] = useState(null);

  // ── wallet top-up modal ──
  const [topupOpen, setTopupOpen] = useState(false);

  // ── invoice filters ──
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('all');

  // ── transaction filters ──
  const [txSearch, setTxSearch] = useState('');

  // ── pay invoice state ──
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [payConfirm, setPayConfirm] = useState(null);

  // ── UPI dialog state ──
  const [upiOpen, setUpiOpen] = useState(false);
  const [upiSubmitting, setUpiSubmitting] = useState(false);
  const [upiSuccess, setUpiSuccess] = useState(false);
  const [upiForm, setUpiForm] = useState({ amount: '', utr_number: '', payment_date: new Date().toISOString().split('T')[0] });
  const [clientPaymentReqs, setClientPaymentReqs] = useState([]);

  // ── invoice view modal ──
  const [invoiceViewDetails, setInvoiceViewDetails] = useState(null);
  const [invoiceViewOpen, setInvoiceViewOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, invoicesRes, walletRes, txRes, settingsRes, clientPayReqsRes] =
        await Promise.allSettled([
          axios.get(`${API}/auth/me`),
          axios.get(`${API}/client/invoices`),
          axios.get(`${API}/wallet/balance`),
          axios.get(`${API}/wallet/transactions`),
          axios.get(`${API}/client/payment-settings`),
          axios.get(`${API}/client/wallet/payment-requests`).catch(() => ({ data: [] })),
        ]);

      const meData = meRes.status === 'fulfilled' ? meRes.value.data : {};
      const isAdminDirect = meData?.created_by_admin_direct ?? false;
      setIsAdminDirectClient(isAdminDirect);
      setClientInfo(meData);

      if (invoicesRes.status === 'fulfilled') setInvoices(invoicesRes.value.data || []);

      if (isAdminDirect) {
        if (walletRes.status === 'fulfilled') setWallet(walletRes.value.data || {});
        if (txRes.status === 'fulfilled') setTransactions(txRes.value.data || []);
        if (settingsRes.status === 'fulfilled') {
          const s = settingsRes.value.data;
          setAutoPayCard(s?.auto_pay_card || false);
          setWalletConsentEnabled(s?.wallet_payment_consent ?? true);
        }
        if (clientPayReqsRes.status === 'fulfilled') setClientPaymentReqs(clientPayReqsRes.value.data || []);
        try {
          const couponRes = await axios.get(`${API}/client/coupon/status`);
          setClientCouponStatus(couponRes.data);
        } catch { /* optional */ }
        try {
          const offerRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/active-client-offer`);
          if (offerRes.data?.id) setActiveServiceOffer(offerRes.data);
        } catch { /* optional */ }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchDomainOrders = async () => {
    setDomainOrdersLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/client/domain-orders`);
      setDomainOrders(res.data?.orders || []);
    } catch { /* silent */ }
    finally { setDomainOrdersLoading(false); }
  };

  useEffect(() => {
    if (topTab === 'domains') fetchDomainOrders();
  }, [topTab]);

  const savePaymentSettings = async (key, val) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/client/payment-settings`, { [key]: val });
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
      if (key === 'auto_pay_card') setAutoPayCard(!val);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleWalletConsent = async (checked) => {
    setWalletConsentEnabled(checked);
    try {
      await axios.put(`${API}/client/wallet-consent`, { wallet_payment_consent: checked });
      toast.success(checked ? 'Wallet auto-pay enabled' : 'Wallet auto-pay disabled. Invoices will be generated for orders.');
    } catch {
      setWalletConsentEnabled(!checked);
      toast.error('Failed to update wallet consent');
    }
  };

  const handleAutoPayCard = (checked) => {
    if (checked) {
      setCardModalOpen(true);
    } else {
      setAutoPayCard(false);
      savePaymentSettings('auto_pay_card', false);
    }
  };

  const handleClientCouponRedeem = async () => {
    if (!clientCouponCode.trim()) { toast.error('Please enter a coupon code'); return; }
    setClientCouponSubmitting(true);
    try {
      await axios.post(`${API}/client/coupon/redeem`, { coupon_code: clientCouponCode.trim() });
      toast.success('Coupon submitted! Admin will review and credit your wallet shortly.');
      setClientCouponStatus({ status: 'pending', coupon_code: clientCouponCode.trim().toUpperCase() });
      setClientCouponCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit coupon');
    } finally {
      setClientCouponSubmitting(false);
    }
  };

  const handleInvoiceDownload = async (invoice) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoice.id}/download`);
      const { pdf_data, filename } = res.data;
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf_data}`;
      link.download = filename || `${invoice.invoice_number}.pdf`;
      link.click();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to download invoice';
      toast.error(msg);
    }
  };

  const handleInvoiceView = async (invoice) => {
    setInvoiceViewDetails(invoice);
    setInvoiceViewOpen(true);
    try {
      const res = await axios.get(`${API}/invoices/${invoice.id}/view-details`);
      setInvoiceViewDetails(prev => ({ ...prev, ...res.data }));
    } catch (_) { /* use basic invoice data */ }
  };

  const handleUpiSubmit = async () => {
    if (!upiForm.amount || !upiForm.utr_number) {
      toast.error('Please fill in amount and UTR number');
      return;
    }
    setUpiSubmitting(true);
    try {
      await axios.post(`${API}/client/wallet/payment-request`, {
        amount: parseFloat(upiForm.amount),
        utr_number: upiForm.utr_number,
        payment_date: upiForm.payment_date,
        notes: '',
      });
      setUpiSuccess(true);
      setUpiForm({ amount: '', utr_number: '', payment_date: new Date().toISOString().split('T')[0] });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payment request');
    } finally {
      setUpiSubmitting(false);
    }
  };

  const handleRazorpayInvoice = async (invoice) => {
    try {
      const orderRes = await axios.post(`${API}/invoices/${invoice.id}/pay`);
      const { order_id, amount_paise, currency, key_id, user: rpUser } = orderRes.data;
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve; script.onerror = reject;
          document.body.appendChild(script);
        });
      }
      const options = {
        key: key_id, amount: amount_paise, currency,
        name: 'CloudSetu', description: `Invoice ${invoice.invoice_number}`,
        order_id,
        prefill: { name: rpUser?.name, email: rpUser?.email, contact: rpUser?.phone },
        theme: { color: '#0ea5e9' },
        handler: async (response) => {
          try {
            await axios.post(`${API}/invoices/${invoice.id}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success(`Payment successful! Invoice ${invoice.invoice_number} is now paid.`);
            fetchAll();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          }
        }
      };
      new window.Razorpay(options).open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handlePayInvoice = async (invoice) => {
    if (!payConfirm) { setPayConfirm(invoice); return; }
    setPayingInvoice(invoice.id);
    try {
      await axios.post(`${API}/client/invoices/${invoice.id}/pay`);
      toast.success('Invoice paid successfully');
      setPayConfirm(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setPayingInvoice(null);
    }
  };

  /* ── filtered invoices ── */
  const filteredInvoices = invoices.filter((inv) => {
    const q = invoiceSearch.toLowerCase();
    const matchSearch = !q || (inv.invoice_number || '').toLowerCase().includes(q);
    const matchStatus =
      invoiceStatus === 'all' ||
      (invoiceStatus === 'paid' && inv.status === 'paid') ||
      (invoiceStatus === 'unpaid' && ['unpaid', 'pending'].includes(inv.status)) ||
      (invoiceStatus === 'cancelled' && ['cancelled', 'voided'].includes(inv.status));
    return matchSearch && matchStatus;
  });

  /* ── filtered transactions ── */
  const filteredTx = transactions.filter((t) => {
    const q = txSearch.toLowerCase();
    return !q ||
      (t.billing_id || '').toLowerCase().includes(q) ||
      (t.id || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q);
  });

  return (
    <DashboardLayout role="client">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* ======================================================= */}
        {/* BILLING & PAYMENTS */}
        {/* ======================================================= */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Billing &amp; Payments</h1>

          {/* Top tabs — Account Information + Billing Details only */}
          <div className="border-b border-slate-200 mb-5">
            <div className="flex gap-6">
              <TabBtn active={topTab === 'account'} onClick={() => setTopTab('account')} testId="tab-account-info">Account Information</TabBtn>
              <TabBtn active={topTab === 'billing'} onClick={() => setTopTab('billing')} testId="tab-billing-details">Billing details</TabBtn>
            </div>
          </div>

          {/* ─── Account Information ─── */}
          {topTab === 'account' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isAdminDirectClient ? (
                <>
                  <Card className="border border-slate-200 shadow-sm">
                    <CardContent className="p-5">
                      <h2 className="font-semibold text-slate-800 mb-4">Account Balance</h2>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <span className="text-sm text-slate-600 w-36 flex-shrink-0">Current</span>
                          <span className="flex-1 border-b border-dashed border-slate-300 mx-2 h-4" />
                          <span className="text-sm font-semibold text-slate-800" data-testid="wallet-balance">{fmt(wallet.balance)}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-sm text-slate-600 w-36 flex-shrink-0">Monthly recurring:</span>
                          <span className="flex-1 border-b border-dashed border-slate-300 mx-2 h-4" />
                          <span className="text-sm font-semibold text-slate-800" data-testid="monthly-recurring">₹0.00</span>
                        </div>
                      </div>
                      <div className="mt-5 flex items-center gap-2 flex-wrap">
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm" data-testid="topup-btn" onClick={() => setTopupOpen(true)}>
                          <Wallet className="h-4 w-4 mr-2" />Top Up
                        </Button>
                        <Button variant="outline" className="text-sm border-blue-300 text-blue-700 hover:bg-blue-600 hover:border-blue-600" data-testid="client-upi-btn" onClick={() => { setUpiOpen(true); setUpiSuccess(false); }}>
                          <QrCode className="h-4 w-4 mr-2" />Add Money via UPI
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border border-slate-200 shadow-sm">
                    <CardContent className="p-5">
                      <h2 className="font-semibold text-slate-800 mb-4">Automatic payments</h2>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm text-slate-700">Wallet payment consent</span>
                            <p className="text-xs text-slate-500 mt-0.5">{walletConsentEnabled ? 'Orders auto-deduct from wallet if sufficient' : 'Invoice will be generated for each order'}</p>
                          </div>
                          <Switch checked={walletConsentEnabled} onCheckedChange={handleWalletConsent} disabled={savingSettings} data-testid="toggle-wallet-consent" />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-700">Automatic payments with a credit card</span>
                          <Switch checked={autoPayCard} onCheckedChange={handleAutoPayCard} disabled={savingSettings} data-testid="toggle-auto-card" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Active Service Offer Banner */}
                  {activeServiceOffer && (
                    <Card className="border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm col-span-1 md:col-span-2" data-testid="service-offer-banner">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                              <Tag className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-slate-800 text-sm">{activeServiceOffer.title || activeServiceOffer.name}</h3>
                                <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-full">Active</span>
                              </div>
                              {activeServiceOffer.description && (
                                <p className="text-xs text-slate-500 mb-2">{activeServiceOffer.description}</p>
                              )}
                              <div className="flex flex-wrap items-center gap-3 mt-1">
                                {activeServiceOffer.discount_type === 'flat' && (
                                  <span className="text-sm font-semibold text-green-700">
                                    ₹{Number(activeServiceOffer.discount_value).toLocaleString('en-IN')} off
                                  </span>
                                )}
                                {activeServiceOffer.discount_type === 'percentage' && (
                                  <span className="text-sm font-semibold text-green-700">
                                    {activeServiceOffer.percentage_value}% off
                                    {activeServiceOffer.max_discount_cap ? ` (max ₹${activeServiceOffer.max_discount_cap})` : ''}
                                  </span>
                                )}
                                {activeServiceOffer.coupon_code && (
                                  <div className="flex items-center gap-1.5 bg-white border border-blue-200 rounded-lg px-3 py-1">
                                    <span className="text-xs text-slate-500">Coupon:</span>
                                    <span className="font-mono text-sm font-bold text-blue-700">{activeServiceOffer.coupon_code}</span>
                                  </div>
                                )}
                                {activeServiceOffer.end_date && (
                                  <span className="text-xs text-slate-500">
                                    Valid till {activeServiceOffer.end_date.split('T')[0]}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Coupon Redemption */}
                  {(!clientCouponStatus || clientCouponStatus.status === 'rejected') && (
                    <Card className="border border-orange-200 bg-orange-50/60 shadow-sm col-span-1 md:col-span-2" data-testid="client-coupon-card">
                      <CardContent className="p-5">
                        <h2 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                          <Tag className="h-4 w-4 text-orange-500" /> Redeem Coupon
                        </h2>
                        <p className="text-sm text-slate-500 mb-3">Enter a valid coupon code to claim wallet credit — admin will verify and credit shortly.</p>
                        {clientCouponStatus?.status === 'rejected' && (
                          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
                            <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-red-700">Coupon Rejected</p>
                              {clientCouponStatus.notes && <p className="text-xs text-red-500 mt-0.5">{clientCouponStatus.notes}</p>}
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2" data-testid="client-coupon-input-area">
                          <input
                            className="flex-1 border border-orange-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                            placeholder="Enter coupon code (e.g. SAVE500)"
                            value={clientCouponCode}
                            onChange={e => setClientCouponCode(e.target.value.toUpperCase())}
                            data-testid="client-coupon-code-input"
                          />
                          <Button
                            className="bg-orange-500 hover:bg-orange-600 text-white shrink-0"
                            disabled={clientCouponSubmitting || !clientCouponCode.trim()}
                            onClick={handleClientCouponRedeem}
                            data-testid="client-redeem-coupon-btn"
                          >
                            {clientCouponSubmitting ? 'Submitting...' : 'Redeem'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {clientCouponStatus?.status === 'pending' && (
                    <Card className="border border-amber-200 bg-amber-50/60 shadow-sm col-span-1 md:col-span-2" data-testid="client-coupon-pending-card">
                      <CardContent className="p-5 flex items-center gap-3">
                        <Clock className="h-5 w-5 text-amber-500 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Coupon Pending Review</p>
                          <p className="text-xs text-amber-600 mt-0.5">Code: <strong>{clientCouponStatus.coupon_code}</strong> — Admin will verify and credit your wallet shortly.</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {clientCouponStatus?.status === 'approved' && (
                    <Card className="border border-green-200 bg-green-50/60 shadow-sm col-span-1 md:col-span-2" data-testid="client-coupon-approved-card">
                      <CardContent className="p-5 flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-green-800">Coupon Approved!</p>
                          <p className="text-xs text-green-600 mt-0.5">₹{(clientCouponStatus.credit_amount || 0).toLocaleString('en-IN')} has been credited to your wallet. Code: <strong>{clientCouponStatus.coupon_code}</strong></p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                /* CASE A: SP/CP created client */
                <div className="col-span-2">
                  <Card className="border border-amber-200 bg-amber-50 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-amber-800 mb-1">Payment handled by your Partner</h3>
                          <p className="text-sm text-amber-700">
                            Your account is managed by your Synergy Partner or Channel Partner. All billing, wallet top-ups, and payment processing are handled on their side. Please contact your partner for any billing queries.
                          </p>
                          <p className="text-xs text-amber-600 mt-2">Your Account ID: <strong>{clientInfo.account_id || '—'}</strong></p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* ─── Billing Details ─── */}
          {topTab === 'billing' && (
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <h2 className="font-semibold text-slate-800 mb-4">Billing Details</h2>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {[
                      ['Business / Account Name', clientInfo.account_name || clientInfo.company_name || '-'],
                      ['Account Type', clientInfo.account_type || '-'],
                      ['Email', clientInfo.email || '-'],
                      ['GST Number', clientInfo.gst_number || '-'],
                      ['Phone', clientInfo.phone || '-'],
                      ['Address', clientInfo.billing_address || clientInfo.address || '-'],
                      ['City', clientInfo.city || '-'],
                      ['State', clientInfo.state_name || '-'],
                      ['Country', (() => {
                        const DIAL_MAP = {'+91':'India','+1':'United States','+44':'United Kingdom','+971':'UAE','+65':'Singapore','+61':'Australia','+49':'Germany','+33':'France','+81':'Japan','+86':'China','+7':'Russia','+55':'Brazil','+27':'South Africa','+92':'Pakistan','+880':'Bangladesh','+974':'Qatar','+966':'Saudi Arabia'};
                        return DIAL_MAP[clientInfo.mobile_country_code] || clientInfo.country || clientInfo.mobile_country_code || '-';
                      })()],
                      ['Pincode', clientInfo.pincode || '-'],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p className="text-slate-500 text-xs mb-0.5">{label}</p>
                        <p className="font-medium text-slate-800">{value}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* ======================================================= */}
        {/* INVOICES / TRANSACTIONS */}
        {/* ======================================================= */}
        <div>
          <h2 className="text-lg font-bold text-slate-800 mb-4">Invoices / Transactions</h2>

          <div className="border-b border-slate-200 mb-5">
            <div className="flex gap-6">
              <TabBtn active={txTab === 'invoices'} onClick={() => setTxTab('invoices')} testId="tab-tx-invoices">Invoices</TabBtn>
              <TabBtn active={txTab === 'transactions'} onClick={() => setTxTab('transactions')} testId="tab-tx-transactions">Transactions</TabBtn>
              <TabBtn active={txTab === 'credits'} onClick={() => setTxTab('credits')} testId="tab-tx-credits">Credit usage history</TabBtn>
              {isAdminDirectClient && (
                <TabBtn active={txTab === 'payment-requests'} onClick={() => setTxTab('payment-requests')} testId="tab-tx-payment-requests">
                  Payment Requests
                  {clientPaymentReqs.filter(r => r.status === 'pending').length > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold">
                      {clientPaymentReqs.filter(r => r.status === 'pending').length}
                    </span>
                  )}
                </TabBtn>
              )}
            </div>
          </div>

          {/* ─── Invoices tab ─── */}
          {txTab === 'invoices' && (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Input
                  placeholder="Search by ID"
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  className="w-40 text-sm"
                  data-testid="invoice-search"
                />
                <div className="flex gap-2">
                  {['all', 'paid', 'unpaid', 'cancelled'].map((s) => (
                    <PillBtn key={s} active={invoiceStatus === s} onClick={() => setInvoiceStatus(s)}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </PillBtn>
                  ))}
                </div>
                <div className="ml-auto">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                    onClick={fetchAll}
                    data-testid="invoice-update-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-1.5" /> Update
                  </Button>
                </div>
              </div>
              <Card className="border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Invoice number</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Status</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Created</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Due date</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Paid on</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Total</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={7} className="px-5 py-12 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                        </td></tr>
                      ) : filteredInvoices.length === 0 ? (
                        <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No invoices found</td></tr>
                      ) : filteredInvoices.map((inv) => {
                        const isUnpaid = ['unpaid', 'pending'].includes(inv.status);
                        const isOverdue = isUnpaid && inv.due_date && new Date(inv.due_date) < new Date();
                        return (
                          <tr key={inv.id} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="px-5 py-3.5 font-semibold text-slate-800 text-nowrap">{inv.invoice_number}</td>
                            <td className="px-5 py-3.5 text-nowrap"><StatusBadge status={inv.status} /></td>
                            <td className="px-5 py-3.5 text-slate-600 text-nowrap">{fmtDate(inv.created_at)}</td>
                            <td className={`text-nowrap px-5 py-3.5 ${isOverdue ? 'text-red-500 font-medium' : 'text-slate-600'}`}>
                              {fmtDate(inv.due_date || inv.created_at)}
                            </td>
                            <td className="px-5 py-3.5 text-nowrap">
                              {inv.status === 'paid' ? (
                                <span className="text-slate-600">{fmtDateTime(inv.paid_at || inv.updated_at)}</span>
                              ) : isUnpaid && isAdminDirectClient && inv.payment_status !== 'invoice_paid' ? (
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-3"
                                  onClick={() => handleRazorpayInvoice(inv)}
                                  disabled={payingInvoice === inv.id}
                                  data-testid={`pay-invoice-${inv.id}`}
                                >
                                  {payingInvoice === inv.id
                                    ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                    : <><CreditCard className="h-3 w-3 mr-1" />Pay via Razorpay</>}
                                </Button>
                              ) : <span className="text-slate-400">-</span>}
                            </td>
                            <td className="text-nowrap px-5 py-3.5 text-right">
                              <p className="font-semibold text-slate-800" data-testid={`invoice-total-${inv.id}`}>{fmt(inv.total_amount || inv.total || inv.amount)}</p>
                              {(inv.subtotal != null && inv.gst_amount != null) ? (
                                <p className="text-xs text-slate-400 mt-0.5">
                                  {fmt(inv.subtotal)} + {fmt(inv.gst_amount)} GST
                                </p>
                              ) : null}
                            </td>
                            <td className="px-5 py-3.5 text-right text-nowrap">
                              {inv.status === 'paid' ? (
                                <Button
                                  size="sm" variant="ghost"
                                  className="text-green-600 hover:text-green-700 h-7 w-7 p-0"
                                  onClick={() => handleInvoiceDownload(inv)}
                                  data-testid={`download-invoice-${inv.id}`}
                                  title="Download PDF"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm" variant="ghost"
                                  className="text-slate-500 hover:text-blue-600 h-7 w-7 p-0"
                                  onClick={() => handleInvoiceView(inv)}
                                  data-testid={`view-invoice-${inv.id}`}
                                  title="View Invoice Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* ─── Transactions tab ─── */}
          {txTab === 'transactions' && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <Input
                  placeholder="Search by ID"
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  className="w-40 text-sm"
                  data-testid="tx-search"
                />
                <div className="ml-auto">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                    onClick={fetchAll}
                    data-testid="tx-update-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-1.5" /> Update
                  </Button>
                </div>
              </div>
              <Card className="border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Invoice number</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Gateway</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Date</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Description</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Total</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Fee</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Debet</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 text-nowrap">Transaction id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={8} className="px-5 py-12 text-center text-nowrap">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                        </td></tr>
                      ) : filteredTx.length === 0 ? (
                        <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500 text-nowrap">No transactions found</td></tr>
                      ) : filteredTx.map((t) => (
                        <tr key={t.id} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="px-5 py-3.5 font-semibold text-slate-800 text-nowrap">{t.billing_id || '-'}</td>
                          <td className="px-5 py-3.5 text-slate-600 capitalize">
                            {t.payment_method || t.transaction_type?.toLowerCase() || 'wallet'}
                          </td>
                          <td className="px-5 py-3.5 text-slate-600 text-nowrap">{fmtDateTime(t.created_at)}</td>
                          <td className="px-5 py-3.5 text-slate-600">{t.description || '-'}</td>
                          <td className="px-5 py-3.5 text-right font-medium text-slate-800 text-nowrap">{fmt(t.amount)}</td>
                          <td className="px-5 py-3.5 text-right text-slate-500 text-nowrap">{fmt(0)}</td>
                          <td className="px-5 py-3.5 text-right text-slate-800 text-nowrap">
                            {t.type === 'debit' ? fmt(t.amount) : fmt(0)}
                          </td>
                          <td className="px-5 py-3.5 text-slate-500 text-xs font-mono break-all max-w-[200px]">{t.id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* ─── Credit Usage History tab ─── */}
          {txTab === 'credits' && (
            <>
              <div className="flex justify-end mb-4">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm" onClick={fetchAll} data-testid="credits-update-btn">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> Update
                </Button>
              </div>
              <Card className="border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500">Date</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500">Description</th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-slate-500">Type</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-slate-500">Amount</th>
                        <th className="text-right px-5 py-3 text-xs font-medium text-slate-500">Balance After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={5} className="px-5 py-12 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                        </td></tr>
                      ) : transactions.length === 0 ? (
                        <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No credit usage history</td></tr>
                      ) : transactions.map((t) => (
                        <tr key={t.id} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="px-5 py-3.5 text-slate-600">{fmtDateTime(t.created_at)}</td>
                          <td className="px-5 py-3.5 text-slate-700">{t.description || '-'}</td>
                          <td className="px-5 py-3.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              t.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                            }`}>
                              {t.type === 'credit' ? 'Credit' : 'Debit'}
                            </span>
                          </td>
                          <td className={`px-5 py-3.5 text-right font-medium ${t.type === 'credit' ? 'text-green-700' : 'text-red-600'}`}>
                            {t.type === 'credit' ? '+' : '-'}{fmt(t.amount)}
                          </td>
                          <td className="px-5 py-3.5 text-right text-slate-700">{fmt(t.balance_after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* ─── Payment Requests tab (admin-direct clients only) ─── */}
          {txTab === 'payment-requests' && isAdminDirectClient && (
            <>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-slate-500">Track the status of your UPI wallet top-up submissions.</p>
                <Button variant="outline" className="text-sm" onClick={() => { setUpiOpen(true); setUpiSuccess(false); }} data-testid="new-upi-request-btn">
                  <QrCode className="h-4 w-4 mr-2" />Add Money via UPI
                </Button>
              </div>
              <Card className="border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        {['Submitted On', 'Amount', 'UTR / Reference', 'Notes', 'Status'].map(h => (
                          <th key={h} className="text-left px-5 py-3 text-xs font-medium text-slate-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={5} className="px-5 py-10 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                        </td></tr>
                      ) : clientPaymentReqs.length === 0 ? (
                        <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                          <QrCode className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                          No payment requests yet. Click "Add Money via UPI" to top up your wallet.
                        </td></tr>
                      ) : clientPaymentReqs.map(r => (
                        <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`payment-request-row-${r.id}`}>
                          <td className="px-5 py-3.5 text-slate-600">{fmtDate(r.created_at)}</td>
                          <td className="px-5 py-3.5 font-semibold text-slate-800">₹{Number(r.amount).toLocaleString('en-IN')}</td>
                          <td className="px-5 py-3.5 font-mono text-xs text-slate-600">{r.utr_number}</td>
                          <td className="px-5 py-3.5 text-slate-500 max-w-[200px] truncate">{r.notes || r.admin_notes || '—'}</td>
                          <td className="px-5 py-3.5">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              r.status === 'approved' ? 'bg-green-100 text-green-700' :
                              r.status === 'rejected' ? 'bg-red-100 text-red-600' :
                              'bg-amber-100 text-amber-700'
                            }`} data-testid={`payment-request-status-${r.id}`}>
                              {r.status?.charAt(0).toUpperCase() + r.status?.slice(1)}
                            </span>
                            {r.status === 'rejected' && r.admin_notes && (
                              <p className="text-xs text-red-500 mt-0.5 max-w-[160px]">{r.admin_notes}</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* =============================== DIALOGS ============================= */}

        {/* Card placeholder modal */}
        <Dialog open={cardModalOpen} onOpenChange={(o) => { if (!o) setCardModalOpen(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Automatic Payment with Credit Card</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Card payment setup coming soon</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Automatic credit card payments via Razorpay will be available shortly.
                    You can use Wallet payment consent in the meantime.
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setCardModalOpen(false)}>Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pay Invoice confirm */}
        <Dialog open={!!payConfirm} onOpenChange={(o) => { if (!o) setPayConfirm(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Confirm Payment</DialogTitle>
            </DialogHeader>
            {payConfirm && (
              <div className="space-y-4 pt-1">
                <p className="text-sm text-slate-700">
                  Pay invoice <strong>#{payConfirm.invoice_number}</strong> for{' '}
                  <strong>{fmt(payConfirm.amount)}</strong> from your wallet?
                </p>
                <p className="text-xs text-slate-500">
                  Available balance: <strong>{fmt(wallet.balance)}</strong>
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setPayConfirm(null)}>Cancel</Button>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => handlePayInvoice(payConfirm)}
                    disabled={payingInvoice === payConfirm.id}
                    data-testid="confirm-pay-invoice"
                  >
                    {payingInvoice === payConfirm.id
                      ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      : 'Confirm Payment'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Wallet Top-up modal */}
        <WalletTopup
          open={topupOpen}
          onOpenChange={setTopupOpen}
          onSuccess={() => { setTopupOpen(false); fetchAll(); }}
        />

        {/* UPI Add Money Dialog */}
        <Dialog open={upiOpen} onOpenChange={(open) => { setUpiOpen(open); if (!open) setUpiSuccess(false); }}>
          <DialogContent className="max-w-3xl p-0 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
                <QrCode className="h-5 w-5" />Add Money via UPI
              </DialogTitle>
              <button onClick={() => { setUpiOpen(false); setUpiSuccess(false); }} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {upiSuccess ? (
              <div className="p-10 flex flex-col items-center justify-center text-center" data-testid="client-upi-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">Payment Request Submitted!</h3>
                <p className="text-slate-600 max-w-sm mb-1">Your payment is being verified and will be automatically added to your wallet once confirmed.</p>
                <p className="text-sm text-slate-500 max-w-sm mb-6">You can track the status in the <span className="font-medium text-blue-600">Payment Requests</span> card below.</p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-sm w-full text-sm text-blue-800 mb-6">
                  <strong>What happens next?</strong>
                  <ul className="mt-2 space-y-1 text-left list-disc list-inside">
                    <li>Admin reviews your UTR number</li>
                    <li>Payment is verified against bank records</li>
                    <li>Amount is credited to your wallet automatically</li>
                  </ul>
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8" onClick={() => { setUpiOpen(false); setUpiSuccess(false); }}>Done</Button>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row">
                <div className="bg-gradient-to-b from-blue-600 to-blue-800 text-white p-8 flex flex-col items-center justify-center md:w-2/5">
                  <div className="bg-white text-blue-800 text-xs font-bold px-3 py-1 rounded-full mb-4">YES BANK</div>
                  <div className="bg-white rounded-xl p-3 mb-4 shadow-lg">
                    <img src="/upi-qrcode.png" alt="UPI QR Code" className="w-40 h-40 object-contain" onError={e => { e.target.style.display='none'; }} />
                  </div>
                  <p className="font-semibold text-sm text-center">Pay to</p>
                  <p className="font-bold text-base text-center mb-1">CLOUDSETU PRIVATE LIMITED</p>
                  <div className="bg-white/20 rounded-lg px-3 py-2 mt-2 text-center">
                    <p className="text-xs text-blue-100">UPI ID</p>
                    <p className="font-mono text-sm font-semibold">yespay.smessi10139493@yesbankltd</p>
                  </div>
                  <div className="flex items-center gap-2 w-full mt-4 mb-1">
                    <div className="flex-1 h-px bg-white/30" />
                    <span className="text-xs text-blue-200 font-medium">or</span>
                    <div className="flex-1 h-px bg-white/30" />
                  </div>
                  <a
                    href={RAZORPAY_PAYMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full mt-3 px-4 py-2.5 rounded-lg bg-white text-blue-800 text-sm font-semibold hover:bg-blue-50 transition-colors"
                    data-testid="client-pay-via-razorpay-btn"
                  >
                    <CreditCard className="h-4 w-4" />
                    Pay via Razorpay
                    <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                  </a>
                </div>
                <div className="flex-1 p-6">
                  <h3 className="font-semibold text-slate-800 mb-1">Enter Payment Details</h3>
                  <p className="text-xs text-slate-500 mb-4">After completing UPI payment, enter your UTR reference number below.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Amount (₹) *</label>
                      <Input type="number" min={1} placeholder="e.g. 5000" value={upiForm.amount} onChange={e => setUpiForm(f => ({ ...f, amount: e.target.value }))} data-testid="client-upi-amount" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">UTR / Transaction Reference *</label>
                      <Input placeholder="12-digit UTR from your UPI app" value={upiForm.utr_number} onChange={e => setUpiForm(f => ({ ...f, utr_number: e.target.value }))} data-testid="client-upi-utr" />
                      <p className="text-xs text-slate-400 mt-0.5">Find this in your UPI app under Transaction History</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Payment Date</label>
                      <Input type="date" value={upiForm.payment_date} onChange={e => setUpiForm(f => ({ ...f, payment_date: e.target.value }))} data-testid="client-upi-date" />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setUpiOpen(false)}>Cancel</Button>
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleUpiSubmit} disabled={upiSubmitting} data-testid="client-upi-submit">
                        {upiSubmitting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : null}
                        Submit Payment
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Invoice View Modal */}
        <Dialog open={invoiceViewOpen} onOpenChange={setInvoiceViewOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Invoice #{invoiceViewDetails?.invoice_number}</DialogTitle>
            </DialogHeader>
            {invoiceViewDetails && (
              <div className="space-y-3 py-1 text-sm">
                <div className="flex justify-between items-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${invoiceViewDetails.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {invoiceViewDetails.status?.toUpperCase()}
                  </span>
                  <span className="text-slate-400 text-xs">{invoiceViewDetails.invoice_date || invoiceViewDetails.created_at?.slice(0,10)}</span>
                </div>

                {invoiceViewDetails.bill_to_company && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Invoiced To</p>
                    <p className="font-semibold">{invoiceViewDetails.bill_to_company}</p>
                    <p className="text-slate-500">{invoiceViewDetails.bill_to_attn}</p>
                    {invoiceViewDetails.bill_to_address && <p className="text-slate-500">{invoiceViewDetails.bill_to_address}</p>}
                    {invoiceViewDetails.bill_to_city && <p className="text-slate-500">{[invoiceViewDetails.bill_to_city, invoiceViewDetails.bill_to_state, invoiceViewDetails.bill_to_pincode].filter(Boolean).join(', ')}</p>}
                  </div>
                )}

                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-700 text-white px-3 py-2 flex justify-between text-xs font-medium">
                    <span>Description</span><span>Total</span>
                  </div>
                  <div className="px-3 py-2 flex justify-between">
                    <span>{invoiceViewDetails.product_name} — {invoiceViewDetails.billing_cycle?.replace(/_/g,' ')}</span>
                    <span>₹{(invoiceViewDetails.subtotal || invoiceViewDetails.amount)?.toLocaleString('en-IN', {minimumFractionDigits:2})}</span>
                  </div>
                </div>

                <div className="space-y-1">
                  {(() => {
                    const gstType = invoiceViewDetails.gst_type || 'IGST';
                    const gstAmt = invoiceViewDetails.gst_amount || 0;
                    const half = Math.round(gstAmt / 2 * 100) / 100;
                    return (
                      <>
                        <div className="flex justify-between text-slate-500"><span>Sub Total</span><span>₹{(invoiceViewDetails.subtotal || invoiceViewDetails.amount)?.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                        {gstType === 'CGST+SGST' ? (
                          <>
                            <div className="flex justify-between text-slate-500"><span>CGST (9%)</span><span>₹{half?.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                            <div className="flex justify-between text-slate-500"><span>SGST (9%)</span><span>₹{half?.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                          </>
                        ) : (
                          <div className="flex justify-between text-slate-500"><span>IGST (18%)</span><span>₹{gstAmt?.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                        )}
                        <div className="flex justify-between font-semibold text-base border-t pt-1"><span>Total</span><span>₹{(invoiceViewDetails.total || invoiceViewDetails.amount)?.toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div>
                      </>
                    );
                  })()}
                </div>

                {invoiceViewDetails.status !== 'paid' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-700 text-xs">
                    PDF will be available automatically once this invoice is marked Paid.
                  </div>
                )}
                {invoiceViewDetails.status === 'paid' && (
                  <div className="flex justify-end">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => { handleInvoiceDownload(invoiceViewDetails); setInvoiceViewOpen(false); }}
                      data-testid="client-download-paid-invoice">
                      <Download className="h-4 w-4 mr-1" /> Download PDF
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
